<script>
import { mapState, mapActions, mapMutations } from 'vuex';
import PageHeader from '@/V2/components/PageHeader.vue';
import {
  iBadge,
  iBadgeColours,
  iButton,
  iButtonStyles,
  iButtonTypes,
  iPagination,
  iMenu,
  iMenuItem,
  iMenuTriggerAlignOptions,
  iModal,
  iTable,
  iTableRow,
  iTableSortButton,
  showToast,
  openModal,
} from '@inscribe/signal';
import EmptyPage from '@/shared/components/EmptyPage.vue';
import { formatDateTime } from '@/utils/date';
import palette from '@inscribe/signal/dist/palette';

export default {
  components: {
    EmptyPage,
    iBadge,
    iButton,
    PageHeader,
    iPagination,
    iMenu,
    iMenuItem,
    iModal,
    iTable,
    iTableRow,
    iTableSortButton,
  },
  data() {
    return {
      iBadgeColours,
      iButtonStyles,
      iButtonTypes,
      iMenuTriggerAlignOptions,
      learnMoreLink: '',
      perPage: 8,
      currentPage: 1,
      loading: false,
      newApiToken: null,
      apiKeyToDeactivate: null,
      palette,
      sortType: 'name',
      sortOrder: 'asc',
    };
  },
  computed: {
    ...mapState(['currentUser']),

    apiKeys() {
      return this.currentUser.organisation.api_keys;
    },

    displayedApiKeys() {
      const displayKeys = this.apiKeys;

      // sort
      const sortValue = 1 * (this.sortOrder === 'asc' ? 1 : -1);
      switch (this.sortType) {
        case 'name':
          displayKeys.sort((k1, k2) =>
            k1.name > k2.name ? 1 * sortValue : -1 * sortValue,
          );
          break;

        case 'last-used':
          displayKeys.sort((k1, k2) =>
            new Date(k1.last_used_at) > new Date(k2.last_used_at)
              ? 1 * sortValue
              : -1 * sortValue,
          );
          break;

        case 'status':
          displayKeys.sort((k1, k2) =>
            k1.active < k2.active ? 1 * sortValue : -1 * sortValue,
          );
          break;
      }

      const paginationStart = this.perPage * (this.currentPage - 1);
      return displayKeys.slice(paginationStart, paginationStart + this.perPage);
    },

    keysLength() {
      return this.apiKeys.length;
    },

    hasApiKeys() {
      return this.keysLength > 0;
    },

    showPagination() {
      return this.keysLength > this.perPage;
    },
  },
  methods: {
    formatDateTime,
    ...mapActions(['deactivateApiKey']),

    ...mapMutations(['changeApiKeyStatus']),

    onOrderChange(field, order) {
      this.sortType = field;
      this.sortOrder = order;
    },

    rowClicked(key) {
      key.active ? this.editKey(key) : this.viewKey(key);
    },

    editKey(data) {
      this.$router.push({
        name: 'api-settings-edit-key',
        params: { api_key: data.key },
      });
    },
    viewKey(data) {
      this.$router.push({
        name: 'api-settings-view-key',
        params: { api_key: data.key },
      });
    },

    redirectToCreateKey() {
      this.$router.push({
        name: 'api-settings-create-key',
      });
    },

    setApiKeyToDeactivate(data) {
      this.apiKeyToDeactivate = data.key;
      openModal('deactivate-key-modal');
    },

    deactivateToken() {
      this.deactivateApiKey(this.apiKeyToDeactivate).then((response) => {
        if (response.data.success) {
          showToast({
            text: 'API Key deactivated',
          });
          this.changeApiKeyStatus(this.apiKeyToDeactivate);
        } else {
          showToast({
            text: `Could not delete API key, error: ${response?.data?.message}.`,
            isError: true,
          });
        }
      });
    },
  },
};
</script>

<template>
  <div class="settings-page d-flex flex-column">
    <PageHeader title="API Keys">
      <template #primary-actions>
        <i-button
          v-if="hasApiKeys"
          text="Create API Key"
          size="md"
          @button-click="redirectToCreateKey()"
        />
      </template>
    </PageHeader>
    <div
      class="settings-page__body d-flex flex-column align-items-center i-px-12 i-py-11"
    >
      <div class="d-flex justify-content-center w-100">
        <div class="settings-page-view">
          <div v-if="currentUser">
            <!-- Empty State with no API Keys -->
            <empty-page
              v-if="!hasApiKeys"
              icon="large-password-key"
              title="Start by creating an API key"
              message="API keys are used to authenticate all requests to the API."
              link="https://docs.inscribe.ai/reference/overview"
              btn-text="Create API Key"
              :btn-click="redirectToCreateKey"
            ></empty-page>
            <!-- Table of Existing API Keys -->
            <div v-else class="d-flex flex-column w-100">
              <i-table>
                <!-- table header -->
                <template #header>
                  <i-table-row type="header">
                    <template #cells>
                      <!-- name -->
                      <div class="no-padding-x">
                        <i-table-sort-button
                          label="Name"
                          class="no-padding-x"
                          @order-change="
                            (order) => onOrderChange('name', order)
                          "
                        ></i-table-sort-button>
                      </div>

                      <!-- last sed -->
                      <div class="no-padding-x">
                        <i-table-sort-button
                          label="Last used"
                          class="no-padding-x"
                          @order-change="
                            (order) => onOrderChange('last-used', order)
                          "
                        ></i-table-sort-button>
                      </div>

                      <!-- date created -->
                      <div>
                        <i-table-sort-button
                          label="Status"
                          @order-change="
                            (order) => onOrderChange('status', order)
                          "
                        ></i-table-sort-button>
                      </div>
                    </template>
                  </i-table-row>
                </template>

                <!-- table body -->
                <template #rows>
                  <i-table-row
                    v-for="apiKey of displayedApiKeys"
                    :key="apiKey.id"
                    class="high selectable"
                    @on-row-click="rowClicked(apiKey)"
                  >
                    <template #cells>
                      <!-- url -->
                      <div class="url-link no-padding-x centered">
                        <router-link
                          class="i-text-subheading-2 i-text-neutral-120"
                          :to="`/settings/api/${apiKey.key}/${
                            apiKey.active ? 'edit' : 'view'
                          }`"
                        >
                          <span v-if="apiKey.name">{{ apiKey.name }}</span>
                          <span v-else>{{ apiKey.key }}</span>
                        </router-link>
                      </div>

                      <!-- event -->
                      <div class="centered">
                        <div class="i-text-body-2 i-text-neutral-120 centered">
                          <span v-if="apiKey.last_used_at">
                            {{ formatDateTime(apiKey.last_used_at, 'lll') }}
                          </span>
                          <span v-else-if="apiKey.just_added">
                            Just added
                          </span>
                          <span v-else> Unused </span>
                        </div>
                      </div>

                      <!-- status -->
                      <div class="centered">
                        <i-badge
                          :colour="
                            apiKey.active
                              ? iBadgeColours.SUCCESS
                              : iBadgeColours.NEUTRAL
                          "
                          :label="apiKey.active ? 'Active' : 'Deactivated'"
                        ></i-badge>
                      </div>

                      <!-- menu -->
                      <div class="menu-container">
                        <i-menu
                          ref="menuEl"
                          :trigger-align="iMenuTriggerAlignOptions.RIGHT"
                          :width="250"
                        >
                          <template #trigger>
                            <i-button
                              class="trigger-button"
                              data-testid="document-actions-button"
                              :type="iButtonTypes.NEUTRAL"
                              :button-style="iButtonStyles.GHOST"
                              :left-icon="'medium-more-menu-vertical'"
                            />
                          </template>

                          <template #items>
                            <!-- View -->
                            <i-menu-item
                              v-if="!apiKey.active"
                              label="View"
                              icon="small-edit-view"
                              @item-click="viewKey(apiKey)"
                            ></i-menu-item>

                            <!-- Edit -->
                            <i-menu-item
                              v-if="apiKey.active"
                              label="Edit"
                              icon="small-edit-pencil"
                              @item-click="editKey(apiKey)"
                            ></i-menu-item>

                            <!-- delete -->
                            <i-menu-item
                              v-if="apiKey.active"
                              label="Deactivate"
                              :icon-color="palette['light-error-90']"
                              :text-color="palette['light-error-90']"
                              icon="small-delete-circle"
                              @item-click="setApiKeyToDeactivate(apiKey)"
                            ></i-menu-item>
                          </template>
                        </i-menu>
                      </div>
                    </template>
                  </i-table-row>
                </template>
              </i-table>

              <i-pagination
                v-if="showPagination"
                size="md"
                :total-items="keysLength"
                :items-per-page="perPage"
                :selected-page="currentPage"
                class="i-mt-12"
                @page-click="(page) => (currentPage = page)"
              ></i-pagination>
            </div>

            <i-modal
              modal-id="deactivate-key-modal"
              title="Deactivate API Key"
              confirm-button-text="Deactivate"
              type="primary"
              @on-submit="deactivateToken"
            >
              <template #body-content
                >Are you sure you want to deactivate this API Key?<br />
                You cannot undo this action.</template
              >
            </i-modal>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
$icon-size: 128px;

.menu-container {
  width: 40px;
}

:deep(i-table) {
  min-height: 300px;
}
</style>
